// Chemin: C:\gestion-copro\src\pages\vigie\AjouterDocumentVigiePage.tsx
// Tunnel dédié ajout document GED — VIGIE
// Session 195 : Création — 3 étapes : Dropzone → Analyse SENTINELLE → Validation → Archivage
// Principe : tout document entrant en GED passe obligatoirement par SENTINELLE
// Réutilise uploadDocument() (porte unique GED) + analyzeDocument (Cloud Function)
// Session 195b : Détection state fromSentinelle — saute depot+analyse, arrive en validation pré-remplie
//                Après archivage → retour SENTINELLE avec message de confirmation dans le chat
// Session 196 : Support XML et XLSX — ACCEPTED_TYPES + ACCEPTED_EXTENSIONS + badges formats + icône XML
// Session 219 : Bloc "Enregistrer le paiement" conditionnel pour quittances de loyer
//               Détection locataire via donneesCles.locataire → matching avec suivi bailleur
//               Option cochée par défaut si locataire trouvé — archivage + enregistrement en une action
// Session 253 : Bandeau "Nouvel interlocuteur détecté" — affiché si suggestion.nouvelInterlocuteur renseigné
//               Bouton "Créer cet interlocuteur" → SENTINELLE avec message pré-rempli (create_interlocuteur)
// Session 254 : Fix P0 — bouton "Créer cet interlocuteur" crée l'interlocuteur en inline (Firestore direct)
//               sans naviguer → le state de validation est préservé, archivage reste possible
//               Fix cosmétique — nom/taille du fichier affichés depuis resultatAnalyseSentinelle en mode fromSentinelle
//               Fix P1 — affaireId transmis à uploadDocument() → rattachement Firestore effectif

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { uploadDocument, checkDuplicateByHash, DuplicateCheckResult } from '../../services/document.service';
import { analyzeDocument as analyzeDocumentService } from '../../services/documentAnalysis.service';
import { TypeDocumentGED, CategorieDocument } from '../../types/document.types';
import { CATEGORIES_DOCUMENT_OPTIONS } from '../../constants/documentTaxonomy';
import { TYPES_DOCUMENT } from '../../services/document.service';
import {
  Upload,
  FileText,
  Sparkles,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  X,
  Edit3,
  Shield,
  ChevronRight,
  CreditCard,
  MapPin,
  UserPlus,
} from 'lucide-react';
import { getLocataires, createPaiement } from '../../services/suiviBailleur.service';
import { Locataire } from '../../types/suiviBailleur.types';
import { createInterlocuteur } from '../../services/interlocuteur.service';
import { TypeInterlocuteur } from '../../types/interlocuteur.types';

// ============================================
// CONSTANTES
// ============================================

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // Session 196 : Formats structurés (XML DPE ADEME, cadastre, urbanisme)
  'text/xml',
  'application/xml',
];
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.xml';

// Catégories disponibles VIGIE (sans Interne CS)
const VIGIE_CATEGORIES = CATEGORIES_DOCUMENT_OPTIONS.filter(
  c => c.value !== 'interne_cs'
);

// ============================================
// TYPES
// ============================================

type Etape = 'depot' | 'analyse' | 'validation' | 'archivage';

interface SuggestionSentinelle {
  type?: TypeDocumentGED;
  typeLabel?: string;
  categorie?: CategorieDocument;
  categorieLabel?: string;
  titre?: string;
  resume?: string;
  dateDocument?: string; // ISO string
  confiance?: number;
  anomalies?: string[];
  alertes?: string[];
  donneesCles?: Record<string, string>;
  sensCorrespondance?: 'entrant' | 'sortant' | null; // Session 297
}

// ============================================
// UTILITAIRES
// ============================================

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 o';
  const k = 1024;
  const sizes = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function AjouterDocumentVigiePage() {
  const { userProfile, firebaseUser } = useAuth();
  const { coproprieteId } = useCopropriete();
  const navigate = useNavigate();
  const location = useLocation();

  // Détection entrée depuis SENTINELLE (resultatAnalyse passé en state)
  const fromSentinelle = location.state?.fromSentinelle === true;
  const resultatAnalyseSentinelle = location.state?.resultatAnalyse ?? null;
  // retourVers : page de retour après archivage (ex: '/vigie/comptabilite')
  // categorieDefaut : pré-sélectionne la catégorie GED (ex: 'comptabilite')
  const retourVers = location.state?.retourVers ?? '/vigie/sentinelle';
  const categorieDefaut = location.state?.categorieDefaut ?? null;

  // Étape courante — si on vient de SENTINELLE, démarrer en validation
  const [etape, setEtape] = useState<Etape>(fromSentinelle ? 'validation' : 'depot');

  // Bloquer l'accès direct sans contexte (SENTINELLE ou page contextuelle)
  useEffect(() => {
    if (!fromSentinelle && !categorieDefaut) {
      navigate('/vigie/sentinelle', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fichier
  const [fichier, setFichier] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Analyse
  const [analysing, setAnalysing] = useState(false);
  const [analyseError, setAnalyseError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<SuggestionSentinelle | null>(null);

  // Validation (champs éditables par l'utilisateur)
  const [titre, setTitre] = useState('');
  const [type, setType] = useState<TypeDocumentGED>('autre');
  const [categorie, setCategorie] = useState<CategorieDocument>('administratif');
  const [sensCorrespondance, setSensCorrespondance] = useState<'entrant' | 'sortant' | null>(null); // Session 297
  const [dateDocument, setDateDocument] = useState('');
  const [description, setDescription] = useState('');
  const [modeEdition, setModeEdition] = useState(false);

  // Archivage
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiveSuccess, setArchiveSuccess] = useState(false);
  // Session 246 : Bouton tantièmes après archivage règlement copropriété
  const [afficherBoutonTantiemes, setAfficherBoutonTantiemes] = useState(false);

  // Session 219 : Bloc bailleur — enregistrement paiement quittance
  const [locataireMatche, setLocataireMatche] = useState<Locataire | null>(null);
  const [enregistrerPaiement, setEnregistrerPaiement] = useState(true);
  const [montantPaiement, setMontantPaiement] = useState('');
  const [moisPaiement, setMoisPaiement] = useState(''); // format YYYY-MM

  // Détection doublon précoce
  const [doublonDetecte, setDoublonDetecte] = useState<DuplicateCheckResult | null>(null);
  const [checkingDoublon, setCheckingDoublon] = useState(false);

  // Session 254 : Création inline interlocuteur
  const [creatingInterlocuteur, setCreatingInterlocuteur] = useState(false);
  const [interlocuteurCree, setInterlocuteurCree] = useState(false);
  const [interlocuteurError, setInterlocuteurError] = useState<string | null>(null);

  // ============================================
  // GESTION FICHIER
  // ============================================

  const validerFichier = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `Fichier trop volumineux (${formatFileSize(file.size)}). Maximum : 10 Mo.`;
    }
    if (!ACCEPTED_TYPES.includes(file.type) && file.type !== '') {
      return 'Format non supporté. Formats acceptés : PDF, images (JPG, PNG), Word, Excel, XML.';
    }
    return null;
  };

  const handleFichierSelectionne = async (file: File) => {
    const erreur = validerFichier(file);
    if (erreur) {
      setAnalyseError(erreur);
      return;
    }
    setAnalyseError(null);
    setDoublonDetecte(null);
    setFichier(file);

    // Vérification doublon précoce — avant toute analyse ou upload
    if (coproprieteId) {
      setCheckingDoublon(true);
      try {
        const hash = await computeFileHash(file);
        const result = await checkDuplicateByHash(coproprieteId, hash, file.name, file.size);
        if (result.isDuplicate) {
          setDoublonDetecte(result);
        }
      } catch {
        // Non bloquant — si la vérification échoue, on laisse passer
      } finally {
        setCheckingDoublon(false);
      }
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFichierSelectionne(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFichierSelectionne(file);
    e.target.value = '';
  };

  // ============================================
  // ÉTAPE 2 : ANALYSE SENTINELLE
  // ============================================

  const lancerAnalyse = async () => {
    if (!fichier || !coproprieteId || !firebaseUser) return;

    setAnalysing(true);
    setAnalyseError(null);
    setEtape('analyse');

    try {
      // 1. Upload temporaire dans Storage pour l'analyse
      const timestamp = Date.now();
      const safeName = fichier.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const tempPath = `coproprietes/${coproprieteId}/temp/${timestamp}_${safeName}`;
      const storageRef = ref(storage, tempPath);
      await uploadBytes(storageRef, fichier);
      const tempUrl = await getDownloadURL(storageRef);

      // 2. Appel Cloud Function analyzeDocument via le service (paramètres corrects)
      const resultat = await analyzeDocumentService(
        tempUrl,
        fichier.name,
        fichier.type || 'application/octet-stream',
        coproprieteId
      );

      if (resultat.success && resultat.suggestion) {
        const s = resultat.suggestion;
        setSuggestion({
          type: s.typeDocument as TypeDocumentGED,
          typeLabel: s.typeDocumentLabel,
          categorie: s.categorie as CategorieDocument,
          categorieLabel: s.categorieLabel,
          titre: s.titre,
          resume: s.description || s.explication,
          dateDocument: s.dateDocument || undefined,
          confiance: s.confidence === 'haute' ? 90 : s.confidence === 'moyenne' ? 65 : 40,
          anomalies: s.anomalies || [],
          alertes: s.actionsRecommandees || [],
          donneesCles: s.donneesCles || {},
          sensCorrespondance: s.sensCorrespondance || null, // Session 297
        });
        setTitre(s.titre || fichier.name.replace(/\.[^.]+$/, ''));
        setType((s.typeDocument as TypeDocumentGED) || 'autre');
        setCategorie((s.categorie as CategorieDocument) || 'administratif');
        setSensCorrespondance(s.sensCorrespondance || null); // Session 297
        if (s.dateDocument) {
          try {
            const d = new Date(s.dateDocument);
            if (!isNaN(d.getTime())) setDateDocument(d.toISOString().split('T')[0]);
          } catch { /* silencieux */ }
        }
        setDescription(s.description || s.explication || '');

        // Session 298 : Garde-fou — email/courrier en Mode A → rediriger vers SENTINELLE
        // SENTINELLE est la seule porte d'entrée pour les correspondances (IC, miroir, croisement)
        const typeDetecte = (s.typeDocument as string) || '';
        if (!fromSentinelle && (typeDetecte === 'email' || typeDetecte === 'courrier')) {
          setEtape('depot');
          setFichier(null);
          setSuggestion(null);
          setAnalyseError(
            'Ce document est une correspondance. Pour bénéficier de l\'analyse complète (croisement historique, relance automatique), dépose-le via SENTINELLE.'
          );
          return;
        }

        setEtape('validation');
      } else {
        // Analyse échouée → saisie manuelle
        setSuggestion(null);
        setTitre(fichier.name.replace(/\.[^.]+$/, ''));
        setType('autre');
        setCategorie('administratif');
        setDateDocument('');
        setDescription('');
        setModeEdition(true);
        setEtape('validation');
      }
    } catch (err: any) {
      console.error('[lancerAnalyse] Erreur:', err);
      // Si l'analyse échoue, on permet quand même la saisie manuelle
      setSuggestion(null);
      setTitre(fichier.name.replace(/\.[^.]+$/, ''));
      setType('autre');
      setCategorie('administratif');
      setDateDocument('');
      setDescription('');
      setModeEdition(true);
      setEtape('validation');
    } finally {
      setAnalysing(false);
    }
  };

  // Pré-remplissage depuis SENTINELLE au montage
  useEffect(() => {
    if (!fromSentinelle || !resultatAnalyseSentinelle) return;
    const { suggestion, fileName } = resultatAnalyseSentinelle;
    setTitre(suggestion.titre || fileName?.replace(/\.[^.]+$/, '') || '');
    setType(suggestion.typeDocument || 'autre');
    setCategorie(suggestion.categorie || 'administratif');
    setSensCorrespondance((suggestion as any).sensCorrespondance || null); // Session 297
    if (suggestion.dateDocument) {
      try {
        const d = new Date(suggestion.dateDocument);
        if (!isNaN(d.getTime())) setDateDocument(d.toISOString().split('T')[0]);
      } catch { /* silencieux */ }
    }
    setDescription(suggestion.description || suggestion.explication || '');
    // Reconstruire la suggestion pour les badges confiance/alertes
    setSuggestion({
      type: suggestion.typeDocument,
      typeLabel: suggestion.typeDocumentLabel,
      categorie: suggestion.categorie,
      titre: suggestion.titre,
      resume: suggestion.explication,
      confiance: suggestion.confidence === 'haute' ? 90 : suggestion.confidence === 'moyenne' ? 65 : 40,
      anomalies: suggestion.anomalies || [],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session 219 : Matching locataire quand le type est quittance_loyer
  useEffect(() => {
    if (!firebaseUser || type !== 'quittance_loyer') {
      setLocataireMatche(null);
      return;
    }
    const chargerEtMatcher = async () => {
      try {
        const locataires = await getLocataires(firebaseUser.uid);
        const actifs = locataires.filter(l => l.actif !== false);
        if (actifs.length === 0) return;

        // Essayer de matcher via donneesCles.locataire (disponible dans le flux SENTINELLE)
        const nomExtrait: string | undefined =
          resultatAnalyseSentinelle?.suggestion?.donneesCles?.locataire;

        let trouve: Locataire | null = null;
        if (nomExtrait) {
          const needle = nomExtrait.toLowerCase().trim();
          trouve = actifs.find(l => {
            const nomComplet = `${l.nom} ${l.prenom ?? ''}`.toLowerCase().trim();
            const nomSeul = l.nom.toLowerCase().trim();
            return nomComplet === needle || nomSeul === needle ||
              nomComplet.includes(needle) || needle.includes(nomSeul);
          }) ?? null;
        }

        // Fallback : un seul locataire actif → match automatique
        if (!trouve && actifs.length === 1) {
          trouve = actifs[0];
        }

        if (trouve) {
          setLocataireMatche(trouve);
          setEnregistrerPaiement(true);
          // Pré-remplir montant depuis donneesCles ou loyer mensuel du locataire
          const montantExtrait = resultatAnalyseSentinelle?.suggestion?.donneesCles?.montantLoyer;
          if (montantExtrait) {
            const montantNum = parseFloat(String(montantExtrait).replace(/[^\d.,]/g, '').replace(',', '.'));
            if (!isNaN(montantNum)) setMontantPaiement(String(montantNum));
          } else if (trouve.loyerMensuel) {
            setMontantPaiement(String(trouve.loyerMensuel));
          }
          // Pré-remplir mois depuis donneesCles.periode (ex: "novembre 2025")
          const periodeExtraite = resultatAnalyseSentinelle?.suggestion?.donneesCles?.periode;
          if (periodeExtraite) {
            const moisMap: Record<string, string> = {
              janvier: '01', février: '02', mars: '03', avril: '04',
              mai: '05', juin: '06', juillet: '07', août: '08',
              septembre: '09', octobre: '10', novembre: '11', décembre: '12'
            };
            const lower = periodeExtraite.toLowerCase().trim();
            const match = lower.match(/([a-zéûôàâ]+)\s+(\d{4})/);
            if (match) {
              const moisNum = moisMap[match[1]];
              if (moisNum) setMoisPaiement(`${match[2]}-${moisNum}`);
            }
          }
        }
      } catch {
        // Non bloquant — le bloc bailleur reste masqué
      }
    };
    chargerEtMatcher();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, firebaseUser]);

  // ============================================
  // SESSION 254 : CRÉATION INLINE INTERLOCUTEUR
  // ============================================

  const handleCreerInterlocuteur = async () => {
    const ni = resultatAnalyseSentinelle?.suggestion?.nouvelInterlocuteur;
    if (!ni || !ni.nom || !coproprieteId) return;

    setCreatingInterlocuteur(true);
    setInterlocuteurError(null);

    try {
      await createInterlocuteur({
        coproprieteId,
        type: (ni.type as TypeInterlocuteur) || 'autre',
        raisonSociale: ni.nom,
        contacts: [{
          nom: ni.nom,
          fonction: ni.fonction || '',
          email: ni.email || '',
          telephone: '',
          principal: true,
        }],
        actif: true,
      });
      setInterlocuteurCree(true);
    } catch (err) {
      console.error('[handleCreerInterlocuteur] Erreur:', err);
      setInterlocuteurError("Erreur lors de la création. Veuillez réessayer.");
    } finally {
      setCreatingInterlocuteur(false);
    }
  };

  // ============================================
  // ÉTAPE 3 : ARCHIVAGE
  // ============================================

  const handleArchiver = async () => {
    if (!titre.trim()) {
      setArchiveError('Le titre est requis.');
      return;
    }
    if (!coproprieteId || !userProfile || !firebaseUser) return;

    // Mode SENTINELLE : fichier déjà dans Storage (tempFilePath) — pas de fichier local nécessaire
    const isModeB = fromSentinelle && resultatAnalyseSentinelle;
    if (!isModeB && !fichier) return;

    setArchiving(true);
    setArchiveError(null);
    setEtape('archivage');

    try {
      const userNom = `${userProfile.prenom || ''} ${userProfile.nom || ''}`.trim() || userProfile.email;

      if (isModeB) {
        // Mode B : fichier déjà uploadé par SENTINELLE dans Storage
        const { suggestion, fileName, fileUrl, fileType, fileSize, fileHash } = resultatAnalyseSentinelle;
        await uploadDocument({
          coproprieteId,
          titre: titre.trim(),
          type,
          categorie,
          description: description.trim() || undefined,
          dateDocument: dateDocument ? new Date(dateDocument) : undefined,
          userId: firebaseUser.uid,
          userNom,
          fileHash,
          tempFilePath: fileUrl,
          fichierNom: fileName,
          fichierTaille: fileSize,
          fichierType: fileType,
          source: 'sentinelle_vigie',
          visibilite: 'proprietaire',
          typeDocumentLabel: suggestion.typeDocumentLabel,
          donneesCles: suggestion.donneesCles || {},
          anomalies: suggestion.anomalies || [],
          analyseSentinelle: {
            confiance: suggestion.confidence,
            explication: suggestion.explication,
            actionsRecommandees: suggestion.actionsRecommandees || [],
            anomalies: suggestion.anomalies || [],
            donneesCles: suggestion.donneesCles || {},
            dateAnalyse: new Date(),
          },
          sensCorrespondance: sensCorrespondance || null, // Session 297 : depuis state
          courrierId_lie: suggestion.courrierId_lie || undefined,
          courrierRef_lie: suggestion.courrierRef_lie || undefined,
          typeCorrespondance: suggestion.typeCorrespondance || undefined,
          qualiteReponse: suggestion.qualiteReponse || undefined,
          pointsTraites: suggestion.pointsTraites || undefined,
          pointsManquants: suggestion.pointsManquants || undefined,
          alerteQualite: suggestion.alerteQualite || undefined,
          // Session 244 : Checklist item + qualité de lecture
          checklistItemId: suggestion.checklistItemId || null,
          qualiteLecture: suggestion.qualiteLecture || null,
          // Session 254 : Rattachement affaire détecté par SENTINELLE
          affaireId: suggestion.affaireId || undefined,
        });
      } else {
        // Mode A : fichier local (flux normal depuis la GED)
        const fileHash = await computeFileHash(fichier!);
        await uploadDocument({
          coproprieteId,
          file: fichier!,
          titre: titre.trim(),
          type,
          categorie,
          description: description.trim() || undefined,
          dateDocument: dateDocument ? new Date(dateDocument) : new Date(),
          userId: firebaseUser.uid,
          userNom,
          fileHash,
          source: 'sentinelle_vigie',
          visibilite: 'proprietaire',
          // Mode A : donneesCles et résumé issus de l'analyse Cloud Function
          donneesCles: suggestion?.donneesCles || {},
          anomalies: suggestion?.anomalies || [],
          sensCorrespondance: sensCorrespondance ?? null, // Session 298 : manquait en Mode A
          ...(suggestion && {
            typeDocumentLabel: suggestion.typeLabel || '',
            analyseSentinelle: {
              confiance: String(suggestion.confiance || ''),
              explication: suggestion.resume || '',
              actionsRecommandees: suggestion.alertes || [],
              anomalies: suggestion.anomalies || [],
              donneesCles: suggestion.donneesCles || {},
              dateAnalyse: new Date(),
            }
          }),
        });
      }

      setArchiveSuccess(true);

      // Session 246 : Détecter règlement copropriété avec tantièmes extraits
      if (isModeB) {
        const donneesCles = resultatAnalyseSentinelle?.suggestion?.donneesCles || {};
        if (type === 'reglement_copropriete' && donneesCles.lotsPropietaire) {
          setAfficherBoutonTantiemes(true);
        }
      }

      // Session 219 : Enregistrer le paiement bailleur si demandé
      if (enregistrerPaiement && locataireMatche && montantPaiement && moisPaiement && firebaseUser) {
        try {
          await createPaiement(firebaseUser.uid, locataireMatche.id, {
            type: 'loyer',
            moisConcerne: moisPaiement,
            montantAttendu: parseFloat(montantPaiement),
            montantRecu: parseFloat(montantPaiement),
            datePaiementEffectif: new Date(),
            note: `Enregistré automatiquement à l'archivage de la quittance "${titre.trim()}"`,
          });
        } catch {
          // Non bloquant — le paiement peut être saisi manuellement dans Suivi bailleur
        }
      }
    } catch (err: any) {
      console.error('[handleArchiver] Erreur:', err);
      if (err.message?.includes('DOUBLON_DETECTE')) {
        setArchiveError('Ce document existe déjà dans votre GED.');
      } else {
        setArchiveError("Une erreur est survenue lors de l'archivage. Veuillez réessayer.");
      }
      setEtape('validation');
    } finally {
      setArchiving(false);
    }
  };

  // ============================================
  // INDICATEUR ÉTAPES
  // ============================================

  const etapes = [
    { key: 'depot', label: 'Dépôt' },
    { key: 'analyse', label: 'Analyse' },
    { key: 'validation', label: 'Validation' },
    { key: 'archivage', label: 'Archivage' },
  ];

  const etapeIndex = etapes.findIndex(e => e.key === etape);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(retourVers)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Ajouter un document</h1>
        <p className="text-sm text-gray-500 mt-1">
          SENTINELLE analyse et classe votre document avant de l'archiver dans votre GED.
        </p>
      </div>

      {/* Indicateur de progression */}
      <div className="flex items-center gap-0 mb-8">
        {etapes.map((e, i) => (
          <React.Fragment key={e.key}>
            <div className="flex flex-col items-center gap-1">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors
                ${i < etapeIndex ? 'bg-green-500 text-white'
                  : i === etapeIndex ? 'bg-blue-900 text-white'
                  : 'bg-gray-200 text-gray-400'}
              `}>
                {i < etapeIndex ? <CheckCircle className="w-5 h-5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${i === etapeIndex ? 'text-blue-900' : 'text-gray-400'}`}>
                {e.label}
              </span>
            </div>
            {i < etapes.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${i < etapeIndex ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ============================== */}
      {/* ÉTAPE 1 : DÉPÔT               */}
      {/* ============================== */}
      {etape === 'depot' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Zone dropzone principale */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !fichier && fileInputRef.current?.click()}
            className={`
              relative min-h-[280px] flex flex-col items-center justify-center cursor-pointer
              transition-all duration-200 select-none
              ${dragOver
                ? 'bg-blue-50'
                : fichier
                  ? 'bg-green-50 cursor-default'
                  : 'bg-gray-50 hover:bg-amber-50/40'}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS}
              onChange={handleInputChange}
              className="hidden"
            />

            {/* Bordure animée drag */}
            <div className={`
              absolute inset-3 rounded-2xl border-2 border-dashed transition-all duration-200 pointer-events-none
              ${dragOver
                ? 'border-blue-400 scale-[0.99]'
                : fichier
                  ? 'border-green-400'
                  : 'border-gray-200'}
            `} />

            {/* Contenu — fichier sélectionné */}
            {fichier ? (
              <div className="flex flex-col items-center gap-4 px-6 py-10 text-center z-10">
                {/* Icône selon type */}
                <div className={`
                  w-20 h-20 rounded-2xl flex items-center justify-center shadow-sm
                  ${fichier.type.includes('pdf') ? 'bg-red-100' :
                    fichier.type.includes('image') ? 'bg-green-100' :
                    fichier.type.includes('word') || fichier.type.includes('document') ? 'bg-blue-100' :
                    fichier.type.includes('sheet') || fichier.type.includes('excel') ? 'bg-emerald-100' :
                    fichier.type.includes('xml') ? 'bg-purple-100' :
                    'bg-gray-100'}
                `}>
                  <FileText className={`w-10 h-10
                    ${fichier.type.includes('pdf') ? 'text-red-500' :
                      fichier.type.includes('image') ? 'text-green-500' :
                      fichier.type.includes('word') || fichier.type.includes('document') ? 'text-blue-500' :
                      fichier.type.includes('sheet') || fichier.type.includes('excel') ? 'text-emerald-500' :
                      fichier.type.includes('xml') ? 'text-purple-500' :
                      'text-gray-400'}
                  `} />
                </div>

                {/* Nom et taille */}
                <div>
                  <p className="font-semibold text-gray-900 text-base max-w-xs truncate">{fichier.name}</p>
                  <p className="text-sm text-gray-500 mt-1">{formatFileSize(fichier.size)}</p>
                </div>

                {/* Badge type */}
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  Prêt pour l'analyse
                </span>

                {/* Changer fichier */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFichier(null);
                    setAnalyseError(null);
                    setDoublonDetecte(null);
                    fileInputRef.current?.click();
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors underline underline-offset-2"
                >
                  <X className="w-3 h-3" /> Changer de fichier
                </button>
              </div>
            ) : (
              /* Contenu — invitation dépôt */
              <div className="flex flex-col items-center gap-5 px-8 py-12 text-center z-10">
                {/* Icône centrale animée au drag */}
                <div className={`
                  w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-200 shadow-sm
                  ${dragOver ? 'bg-blue-100 scale-110' : 'bg-white border-2 border-gray-200'}
                `}>
                  <Upload className={`w-9 h-9 transition-colors duration-200 ${dragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>

                {/* Texte principal */}
                <div>
                  <p className={`text-lg font-semibold transition-colors duration-200 ${dragOver ? 'text-blue-700' : 'text-gray-800'}`}>
                    {dragOver ? 'Relâchez pour déposer' : 'Glissez votre document ici'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    ou <span className="text-amber-600 font-medium underline underline-offset-2">cliquez pour parcourir</span>
                  </p>
                </div>

                {/* Formats acceptés */}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                  {[
                    { label: 'PDF', color: 'bg-red-50 text-red-600 border-red-200' },
                    { label: 'JPG / PNG', color: 'bg-green-50 text-green-600 border-green-200' },
                    { label: 'Word', color: 'bg-blue-50 text-blue-600 border-blue-200' },
                    { label: 'Excel', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
                    { label: 'XML', color: 'bg-purple-50 text-purple-600 border-purple-200' },
                  ].map(f => (
                    <span key={f.label} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${f.color}`}>
                      {f.label}
                    </span>
                  ))}
                  <span className="text-xs text-gray-400 ml-1">— max 10 Mo</span>
                </div>
              </div>
            )}
          </div>

          {/* Pied de carte — erreur + bouton */}
          <div className="px-6 pb-6 pt-4 space-y-3">
            {/* Erreur validation fichier */}
            {analyseError && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-800">{analyseError}</p>
                    {analyseError.includes('correspondance') && (
                      <button
                        onClick={() => navigate('/vigie/sentinelle', { state: { messagePreRempli: "J'ai reçu un nouveau document, peux-tu l'analyser ?" } })}
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-semibold rounded-lg hover:bg-blue-800 transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Ouvrir SENTINELLE
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Vérification doublon en cours */}
            {checkingDoublon && (
              <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                Vérification des doublons…
              </div>
            )}

            {/* Avertissement doublon détecté */}
            {doublonDetecte && !checkingDoublon && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Ce document existe déjà dans votre GED</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      "{doublonDetecte.existingDocumentTitre}"
                      {doublonDetecte.existingDocumentDate && (
                        <> — archivé le {doublonDetecte.existingDocumentDate.toLocaleDateString('fr-FR')}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setFichier(null);
                      setDoublonDetecte(null);
                      setAnalyseError(null);
                    }}
                    className="flex-1 py-2 px-3 border border-amber-300 rounded-lg text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => setDoublonDetecte(null)}
                    className="flex-1 py-2 px-3 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                  >
                    Archiver quand même
                  </button>
                </div>
              </div>
            )}

            {/* Bouton lancer analyse */}
            <button
              onClick={lancerAnalyse}
              disabled={!fichier || checkingDoublon || !!doublonDetecte}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm text-base"
            >
              <Sparkles className="w-5 h-5" />
              Analyser avec SENTINELLE
            </button>
          </div>
        </div>
      )}

      {/* ============================== */}
      {/* ÉTAPE 2 : ANALYSE EN COURS     */}
      {/* ============================== */}
      {etape === 'analyse' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-amber-600 animate-pulse" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">SENTINELLE analyse votre document</h2>
          <p className="text-gray-500 text-sm mb-6">
            Identification du type, de la catégorie, extraction des informations clés…
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Analyse en cours, veuillez patienter…
          </div>
        </div>
      )}

      {/* ============================== */}
      {/* ÉTAPE 3 : VALIDATION           */}
      {/* ============================== */}
      {etape === 'validation' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

          {/* Badge résultat analyse */}
          {suggestion ? (
            <div className="flex items-center gap-3 mb-6 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="w-9 h-9 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">SENTINELLE a classé votre document</p>
                <p className="text-xs text-amber-700">
                  Vérifiez et corrigez si nécessaire avant d'archiver.
                </p>
              </div>
              {suggestion.confiance !== undefined && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  suggestion.confiance >= 80 ? 'bg-green-100 text-green-700'
                    : suggestion.confiance >= 50 ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {suggestion.confiance}% confiance
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-6 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Edit3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-900">Saisie manuelle</p>
                <p className="text-xs text-blue-700">L'analyse automatique n'a pas pu aboutir. Renseignez les informations ci-dessous.</p>
              </div>
            </div>
          )}

          {/* Alertes SENTINELLE */}
          {suggestion?.alertes && suggestion.alertes.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl space-y-1">
              <p className="text-xs font-semibold text-red-700 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Points d'attention détectés
              </p>
              {suggestion.alertes.map((alerte, i) => (
                <p key={i} className="text-xs text-red-600 ml-5">• {alerte}</p>
              ))}
            </div>
          )}

          {/* Session 254 : Bandeau interlocuteur inconnu — création inline Firestore */}
          {(() => {
            const ni = resultatAnalyseSentinelle?.suggestion?.nouvelInterlocuteur;
            if (!ni || !ni.nom) return null;
            const detail = [ni.fonction, ni.societe].filter(Boolean).join(' — ');
            return (
              <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <UserPlus className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    {interlocuteurCree ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <p className="text-sm font-semibold text-green-800">
                          {ni.nom} ajouté à vos contacts
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-violet-900">
                          Nouvel interlocuteur détecté
                        </p>
                        <p className="text-xs text-violet-700 mt-0.5">
                          <span className="font-medium">{ni.nom}</span>
                          {detail ? ` — ${detail}` : ''}
                          {ni.email ? ` (${ni.email})` : ''}
                        </p>
                        {interlocuteurError && (
                          <p className="text-xs text-red-600 mt-1">{interlocuteurError}</p>
                        )}
                        <button
                          onClick={handleCreerInterlocuteur}
                          disabled={creatingInterlocuteur}
                          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {creatingInterlocuteur ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Création en cours…
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-3.5 h-3.5" />
                              Créer cet interlocuteur
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Fichier sélectionné */}
          <div className="flex items-center gap-3 mb-5 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {fromSentinelle ? (resultatAnalyseSentinelle?.fileName || 'Document') : fichier?.name}
              </p>
              <p className="text-xs text-gray-400">
                {fromSentinelle
                  ? (resultatAnalyseSentinelle?.fileSize ? formatFileSize(resultatAnalyseSentinelle.fileSize) : 'Taille non disponible')
                  : formatFileSize(fichier?.size || 0)}
              </p>
            </div>
          </div>

          {/* Formulaire validation */}
          <div className="space-y-4">

            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre du document <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={titre}
                onChange={e => setTitre(e.target.value)}
                placeholder="Ex: Appel de fonds 1er trimestre 2026"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Type + Catégorie */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={type}
                  onChange={e => setType(e.target.value as TypeDocumentGED)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {TYPES_DOCUMENT.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                <select
                  value={categorie}
                  onChange={e => setCategorie(e.target.value as CategorieDocument)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {VIGIE_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Session 297 : Sens correspondance — visible uniquement pour email/courrier */}
            {(type === 'email' || type === 'courrier') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sens
                  <span className="ml-2 text-xs text-gray-400 font-normal">— document reçu ou envoyé ?</span>
                </label>
                <select
                  value={sensCorrespondance || ''}
                  onChange={e => setSensCorrespondance(e.target.value as 'entrant' | 'sortant' || null)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">— Non précisé</option>
                  <option value="entrant">Reçu (document entrant)</option>
                  <option value="sortant">Envoyé (document sortant)</option>
                </select>
              </div>
            )}

            {/* Date document */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date du document</label>
              <input
                type="date"
                value={dateDocument}
                onChange={e => setDateDocument(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            {/* Description / résumé */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
                {suggestion?.resume && (
                  <span className="ml-2 text-xs text-amber-600 font-normal">— résumé SENTINELLE</span>
                )}
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Informations complémentaires sur ce document…"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              />
            </div>
          </div>

          {/* Erreur archivage */}
          {archiveError && (
            <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {archiveError}
            </div>
          )}

          {/* Session 219 : Bloc bailleur — enregistrement paiement quittance */}
          {locataireMatche && (
            <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CreditCard className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="cb-paiement-bailleur"
                      checked={enregistrerPaiement}
                      onChange={e => setEnregistrerPaiement(e.target.checked)}
                      className="w-4 h-4 rounded accent-emerald-600"
                    />
                    <label htmlFor="cb-paiement-bailleur" className="text-sm font-semibold text-emerald-800 cursor-pointer">
                      Enregistrer le paiement de {locataireMatche.prenom ? `${locataireMatche.prenom} ${locataireMatche.nom}` : locataireMatche.nom}
                    </label>
                  </div>
                  {enregistrerPaiement && (
                    <div className="flex gap-3 mt-2">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-emerald-700 mb-1">Montant (€)</label>
                        <input
                          type="number"
                          value={montantPaiement}
                          onChange={e => setMontantPaiement(e.target.value)}
                          placeholder="Ex: 1010"
                          className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-white"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-emerald-700 mb-1">Mois concerné</label>
                        <input
                          type="month"
                          value={moisPaiement}
                          onChange={e => setMoisPaiement(e.target.value)}
                          className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => {
                if (fromSentinelle) {
                  navigate('/vigie/sentinelle');
                } else if (categorieDefaut) {
                  navigate(retourVers);
                } else {
                  setEtape('depot');
                  setFichier(null);
                  setSuggestion(null);
                  setAnalyseError(null);
                }
              }}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {fromSentinelle ? 'Annuler' : 'Recommencer'}
            </button>
            <button
              onClick={handleArchiver}
              disabled={!titre.trim() || archiving}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-900 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {archiving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Archivage en cours…
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  {locataireMatche && enregistrerPaiement ? 'Archiver et enregistrer le paiement' : 'Archiver dans ma GED'}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ============================== */}
      {/* ÉTAPE 4 : ARCHIVAGE EN COURS   */}
      {/* ============================== */}
      {etape === 'archivage' && !archiveSuccess && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-blue-700 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Archivage en cours…</h2>
          <p className="text-gray-400 text-sm">Votre document est en cours d'enregistrement dans votre GED.</p>
        </div>
      )}

      {/* ============================== */}
      {/* SUCCÈS                         */}
      {/* ============================== */}
      {archiveSuccess && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Document archivé !</h2>
          <p className="text-gray-500 text-sm mb-8">
            <span className="font-medium text-gray-900">"{titre}"</span> est maintenant disponible dans votre GED.
          </p>
          {/* Session 246 : Bouton mise à jour tantièmes */}
          {afficherBoutonTantiemes && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800 mb-1">Tantièmes détectés dans ce règlement</p>
                  <p className="text-xs text-amber-700 mb-3">
                    SENTINELLE a extrait vos tantièmes depuis ce document. Mettez à jour votre profil en un clic.
                  </p>
                  <button
                    onClick={() => navigate('/vigie/sentinelle', {
                      state: {
                        messagePreRempli: `Mets à jour mes tantièmes depuis le règlement de copropriété que je viens d'archiver dans ma GED : "${titre}".`
                      }
                    })}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    <MapPin className="w-4 h-4" />
                    Mettre à jour mes tantièmes
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(retourVers)}
              className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <button
              onClick={() => navigate('/vigie/sentinelle')}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-900 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors text-sm"
            >
              <Sparkles className="w-4 h-4" />
              Ouvrir SENTINELLE
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
